import { Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { SiteLocale } from 'constants/i18n.constants';
import { useLocale } from 'hooks/useLocale';
import { useAccount } from 'restful/accounts';
import { DEFAULT_CURRENCY, useTicketsCart } from 'restful/tickets';

import BorderStack from 'components/@common/BorderStack';
import Icon from 'components/@icons/Icon';
import FormattedMoneyInfo from 'components/@tickets/FormattedMoneyInfo';

import { cartHasDiscount } from './utils';

interface Props {
  onOpenOrderSummary: () => void;
  onCheckout: () => void;
  isLoadingCheckout: boolean;
  isLoyaltyEffectLoading: boolean;
  cartItemsCount: number;
  cartTotalOriginalPrice: number;
  cartTotalDiscountedPrice?: number;
  variant?: 'orderSummary' | 'checkout';
}

const CartCheckoutButton = ({
  isLoadingCheckout,
  isLoyaltyEffectLoading,
  cartItemsCount,
  cartTotalOriginalPrice,
  cartTotalDiscountedPrice,
  variant = 'checkout',
  onOpenOrderSummary,
  onCheckout,
}: Props) => {
  const { locale } = useLocale();
  const { cart } = useTicketsCart();
  const { account, isLoading: isAccountLoading } = useAccount();

  const discount = useMemo(() => {
    return cartHasDiscount(cart?.modifiers || null);
  }, [cart?.modifiers]);

  const isDiscounted =
    !!cartTotalDiscountedPrice &&
    cartTotalOriginalPrice !== cartTotalDiscountedPrice &&
    !!discount;

  const isCheckout = variant === 'checkout';

  return (
    <>
      <BorderStack
        sx={(theme) => ({
          p: isCheckout ? { xs: 0, md: 2 } : 0,
          flexGrow: 1,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: isCheckout ? { md: 'grey.900' } : undefined,
          border: isCheckout
            ? { xs: 'none', md: `1px solid ${theme.vars.palette.divider}` }
            : 'none',
        })}
      >
        {(!!account || isAccountLoading) && !!discount?.value && (
          <Typography
            sx={{
              color: 'text.secondary',
              mb: { xs: 2, md: 1 },
            }}
            variant="small"
          >
            {isAccountLoading ? (
              <Skeleton sx={{ backgroundColor: 'grey.800', width: '65%' }} />
            ) : (
              <FormattedMessage id="general.discount.label" />
            )}
          </Typography>
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: { xs: 2, md: 3 },
            maxHeight: 48,
          }}
        >
          {cartItemsCount > 0 && !!account ? (
            <BorderStack
              onClick={isCheckout ? onOpenOrderSummary : undefined}
              sx={{
                cursor: isCheckout ? 'pointer' : undefined,
                px: isCheckout ? { xs: 1.5, md: 2 } : 0,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'row',
                justifyContent: 'space-between',
                flexGrow: 1,
                minWidth: '60%',
                border: isCheckout ? undefined : 'none',
              }}
            >
              <Stack sx={{ flexGrow: 1 }}>
                <Typography
                  variant="extraSmall"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                  }}
                >
                  <FormattedMessage
                    id="general.tickets.label"
                    values={{ tickets: cartItemsCount }}
                  />
                </Typography>
                {isLoyaltyEffectLoading ? (
                  <Typography
                    variant="extraSmall"
                    fontWeight={500}
                    sx={{ width: '60%' }}
                  >
                    <Skeleton sx={{ backgroundColor: 'grey.800' }} />
                  </Typography>
                ) : (
                  <Box
                    display="flex"
                    flexDirection={
                      locale === SiteLocale.AR ? 'row-reverse' : 'row'
                    }
                    alignSelf="flex-start"
                  >
                    <Typography variant="extraSmall" fontWeight={500} mr="2px">
                      <FormattedMessage
                        id={`currency.${DEFAULT_CURRENCY.toLowerCase()}.label`}
                      />
                    </Typography>
                    {isDiscounted && (
                      <Typography
                        variant="extraSmall"
                        sx={{
                          fontWeight: 500,
                          textDecoration: 'line-through',
                          mr: 0.5,
                        }}
                      >
                        <FormattedMoneyInfo
                          price={cartTotalOriginalPrice}
                          style="decimal"
                        />
                      </Typography>
                    )}
                    <Typography
                      variant="extraSmall"
                      fontWeight={500}
                      sx={{
                        ...(isDiscounted && { color: 'text.secondary' }),
                        ...(!isDiscounted && { color: 'text.primary' }),
                      }}
                    >
                      <FormattedMoneyInfo
                        price={cartTotalDiscountedPrice}
                        style="decimal"
                      />
                    </Typography>
                  </Box>
                )}
              </Stack>
              {isCheckout && <Icon name="ChevronDown" />}
            </BorderStack>
          ) : (
            <Typography
              variant="extraSmall"
              sx={{
                color: 'grey.500',
                flexGrow: 1,
              }}
            >
              <FormattedMessage id="event.seating.make_selection" />
            </Typography>
          )}
          <Button
            variant="outlined"
            color="secondary"
            onClick={onCheckout}
            loading={isLoadingCheckout}
            disabled={cartItemsCount === 0}
            sx={{ flexGrow: { md: 1 }, whiteSpace: 'nowrap' }}
          >
            <FormattedMessage id="cart.checkout.button" />
          </Button>
        </Box>
      </BorderStack>
    </>
  );
};
export default CartCheckoutButton;
